import api from './client';
import type {
  ListingFeedResponse,
  ListingDetailResponse,
  ListingPhoto,
  MarketMetricsResponse,
  ListingFilters,
  MapPinResponse,
  ScoreListingResponse,
  PipelineRunSingleResponse,
  PipelineSummaryResponse,
  HotDealsResponse,
  WatchlistEntryResponse,
  ListingClassifyResponse,
  QueueForEvaluationResponse,
  BulkQueueResponse,
} from '../types/listing';

const AI_TIMEOUT = 120_000; // 2 min for Vision scoring / pipeline

// ── Listing Feed ──

export async function listListings(filters?: ListingFilters): Promise<ListingFeedResponse> {
  const params: Record<string, string | number | boolean> = {};
  if (filters?.zip_codes) params.zip_codes = filters.zip_codes;
  if (filters?.status) params.status = filters.status;
  if (filters?.min_price != null) params.min_price = filters.min_price;
  if (filters?.max_price != null) params.max_price = filters.max_price;
  if (filters?.search) params.search = filters.search;
  if (filters?.bbox) params.bbox = filters.bbox;
  if (filters?.sort_by) params.sort_by = filters.sort_by;
  if (filters?.sort_desc != null) params.sort_desc = filters.sort_desc;
  if (filters?.limit != null) params.limit = filters.limit;
  if (filters?.offset != null) params.offset = filters.offset;
  if (filters?.watchlist_only) params.watchlist_only = true;
  if (filters?.min_opp_score != null) params.min_opp_score = filters.min_opp_score;
  if (filters?.max_opp_score != null) params.max_opp_score = filters.max_opp_score;
  if (filters?.opp_tier) params.opp_tier = filters.opp_tier;

  const { data } = await api.get<ListingFeedResponse>('/listings', { params });
  return data;
}

export async function listMapPins(filters?: ListingFilters): Promise<MapPinResponse[]> {
  const params: Record<string, string | number | boolean> = {};
  if (filters?.zip_codes) params.zip_codes = filters.zip_codes;
  if (filters?.status) params.status = filters.status;
  if (filters?.min_price != null) params.min_price = filters.min_price;
  if (filters?.max_price != null) params.max_price = filters.max_price;
  if (filters?.search) params.search = filters.search;
  if (filters?.bbox) params.bbox = filters.bbox;

  const { data } = await api.get<MapPinResponse[]>('/listings/map-pins', { params });
  return data;
}

// ── Listing Detail ──

export async function getListing(listingId: string): Promise<ListingDetailResponse> {
  const { data } = await api.get<ListingDetailResponse>(`/listings/${listingId}`);
  return data;
}

// ── Listing Photos ──

export async function getListingPhotos(listingId: string): Promise<ListingPhoto[]> {
  const { data } = await api.get<ListingPhoto[]>(`/listings/${listingId}/photos`);
  return data;
}

// ── Create Analysis from Listing ──

export async function createAnalysisFromListing(
  listingId: string,
): Promise<{ analysis_id: string; address: string; message: string }> {
  const { data } = await api.post<{ analysis_id: string; address: string; message: string }>(
    `/listings/${listingId}/create-analysis`,
  );
  return data;
}

// ── Market Metrics ──

export async function getMarketMetrics(
  zipCodes?: string,
  days?: number,
): Promise<MarketMetricsResponse> {
  const params: Record<string, string | number> = {};
  if (zipCodes) params.zip_codes = zipCodes;
  if (days != null) params.days = days;

  const { data } = await api.get<MarketMetricsResponse>('/listings/market-metrics', { params });
  return data;
}

// ── Phase 2: Scoring + Pipeline ──

export async function scoreListing(listingId: string): Promise<ScoreListingResponse> {
  const { data } = await api.post<ScoreListingResponse>(`/listings/${listingId}/score`, null, {
    timeout: AI_TIMEOUT,
  });
  return data;
}

export async function runPipeline(listingId: string): Promise<PipelineRunSingleResponse> {
  const { data } = await api.post<PipelineRunSingleResponse>(
    `/listings/${listingId}/run-pipeline`,
    null,
    { timeout: AI_TIMEOUT },
  );
  return data;
}

export async function uploadPhotos(
  listingId: string,
  files: File[],
): Promise<{ listing_id: string; photos_uploaded: number; message: string }> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const { data } = await api.post<{ listing_id: string; photos_uploaded: number; message: string }>(
    `/listings/${listingId}/photos/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function getPipelineSummary(): Promise<PipelineSummaryResponse> {
  const { data } = await api.get<PipelineSummaryResponse>('/listings/pipeline-summary');
  return data;
}

// ── Hot Deals ──

export async function getHotDeals(): Promise<HotDealsResponse> {
  const { data } = await api.get<HotDealsResponse>('/listings/hot-deals');
  return data;
}

// ── Watchlist ──

export async function addToWatchlist(
  listingId: string,
  body?: { notes?: string; tags?: string[] },
): Promise<WatchlistEntryResponse> {
  const { data } = await api.post<WatchlistEntryResponse>(`/watchlist/${listingId}`, body || {});
  return data;
}

export async function removeFromWatchlist(listingId: string): Promise<void> {
  await api.delete(`/watchlist/${listingId}`);
}

export async function bulkAddToWatchlist(
  listingIds: string[],
): Promise<{ added: number; message: string }> {
  const { data } = await api.post<{ added: number; message: string }>('/watchlist/bulk-add', {
    listing_ids: listingIds,
  });
  return data;
}

export async function bulkDismiss(
  listingIds: string[],
): Promise<{ dismissed: number; message: string }> {
  const { data } = await api.post<{ dismissed: number; message: string }>('/watchlist/bulk-dismiss', {
    listing_ids: listingIds,
  });
  return data;
}

// ── Phase 3: Classify + Queue ──

export async function classifyListing(listingId: string): Promise<ListingClassifyResponse> {
  const { data } = await api.post<ListingClassifyResponse>(`/listings/${listingId}/classify`);
  return data;
}

export async function queueForEvaluation(listingId: string): Promise<QueueForEvaluationResponse> {
  const { data } = await api.post<QueueForEvaluationResponse>(`/listings/${listingId}/queue`);
  return data;
}

export async function bulkQueueForEvaluation(listingIds: string[]): Promise<BulkQueueResponse> {
  const { data } = await api.post<BulkQueueResponse>('/listings/bulk-queue', {
    listing_ids: listingIds,
  });
  return data;
}
